import PillButton from "../PillButton";
import { Box, Divider, Typography } from "@mui/material";
import ControlledTextField from "@/app/(browse)/sell/ad/form/ControlledTextField";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { CreateContactCase } from "@/case/actions/CreateContactCase";
import {
  CREATE_CONTACT_CASE_VIEW_MODEL_SCHEMA,
  CreateContactCaseViewModel,
} from "@/case/models/CreateContactCaseViewModel";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContactDetails } from "@/app/layout/useContactDetails";
import { useEffect } from "react";

export function ContactForm() {
  const { contactDetails } = useContactDetails();
  const methods = useForm<CreateContactCaseViewModel>({
    resolver: zodResolver(CREATE_CONTACT_CASE_VIEW_MODEL_SCHEMA),
    defaultValues: {
      name: "",
      details: "",
      emailOrPhone: "",
    },
  });

  useEffect(() => {
    methods.setValue("name", contactDetails?.name || "");
    methods.setValue("emailOrPhone", contactDetails?.email || "");
  }, [contactDetails, methods]);

  const onSubmit: SubmitHandler<CreateContactCaseViewModel> = async (data) => {
    await CreateContactCase(data);
    methods.reset();
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {methods.formState.isSubmitSuccessful ? (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography>Thank you.</Typography>
              <Typography variant="body2">
                We will take a look within 1 business day, and get back to you
                if you&apos;ve left contact details and the issue requires a response
                or we need more information.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography variant="body2">
                  Please drop us a line below with any issues, questions,
                  suggestions or concerns.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <ControlledTextField
                name="details"
                label="Your Message"
                rows={4}
                required
              />
              <ControlledTextField
                name="name"
                label="Name"
                required
              />
              <ControlledTextField
                name="emailOrPhone"
                label="Email / Phone"
                required
              />
            </Box>
            <PillButton
              sx={{
                alignSelf: "end",
              }}
              type="submit"
              primary
              loading={methods.formState.isSubmitting}
              disabled={methods.formState.isSubmitting}
            >
              Send
            </PillButton>

            <Divider>
              <Typography>or, reach out directly:</Typography>
            </Divider>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography
                component="a"
                href="mailto:support@horsezone.com.au"
                sx={{ textDecoration: "underline" }}
              >
                support@horsezone.com.au
              </Typography>
              <Box>
                <Typography
                  component="a"
                  href="tel:+61400105555"
                  sx={{ textDecoration: "underline", display: "inline" }}
                >
                  0400 105 555
                </Typography>{" "}
                <Typography variant="caption">
                  (Monday to Friday, 9am-5pm AEST)
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </FormProvider>
  );
}
